import React from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Bell, Mail, Eye } from 'lucide-react'

import { PageHeader } from '@/components/shared'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { notificationService } from '@/services/notificationService'

const QUERY_KEY = 'notification-preferences'
const CHANNELS = [
  { key: 'in_app', label: 'In the app', icon: Bell },
  { key: 'email', label: 'Email', icon: Mail }
]

// /profile/notifications — which Project Central notifications reach the bell and the inbox
const NotificationSettings = () => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery(QUERY_KEY, notificationService.getPreferences)
  const types = data?.data?.types || []
  const preferences = data?.data?.preferences || {}

  const update = async (type, channel, value) => {
    const previous = queryClient.getQueryData(QUERY_KEY)
    queryClient.setQueryData(QUERY_KEY, (old) => ({
      ...old,
      data: {
        ...old.data,
        preferences: { ...old.data.preferences, [type]: { ...old.data.preferences[type], [channel]: value } }
      }
    }))
    try {
      queryClient.setQueryData(QUERY_KEY, await notificationService.updatePreferences({ [type]: { [channel]: value } }))
    } catch (err) {
      queryClient.setQueryData(QUERY_KEY, previous)
      toast.error(err.response?.data?.error || 'Could not save your notification settings')
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Notification settings"
        description="Choose which Project Central updates reach you in the bell and by email. You never get notified about your own changes."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">What you're notified about</CardTitle>
          <CardDescription>Changes save as soon as you flip a switch.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 px-6 pb-2 text-xs font-medium text-muted-foreground">
            <span />
            {CHANNELS.map(({ key, label, icon: Icon }) => (
              <span key={key} className="flex w-16 flex-col items-center gap-1 text-center"><Icon className="h-4 w-4" />{label}</span>
            ))}
          </div>
          <div className="divide-y border-t">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="px-6 py-4"><Skeleton className="h-8 w-full" /></div>)
              : types.map(t => (
                <div key={t.type} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 px-6 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.description}</p>
                  </div>
                  {CHANNELS.map(({ key, label }) => (
                    <div key={key} className="flex w-16 justify-center">
                      <Switch
                        checked={!!preferences[t.type]?.[key]}
                        onCheckedChange={(value) => update(t.type, key, value)}
                        aria-label={`${t.label}: ${label}`}
                      />
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" /> Watching tasks</CardTitle>
          <CardDescription>
            Comments and status changes come from tasks you watch. You start watching a task when you create it, are assigned
            to it, comment on it or are @mentioned in it. Use <strong>Watch</strong> at the top of any task to follow or unfollow it,
            or to add a teammate.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}

export default NotificationSettings
